import { ComponentProps, ReactNode } from "react";

import { twMerge } from "tailwind-merge";
import {
  ConnectorAlreadyConnectedError,
  useAccount,
  useChainId,
  useConnect,
  useDisconnect,
  useSwitchChain,
} from "wagmi";

import { Button, ButtonProps } from "shared/ui/button";
import { Drawer } from "shared/ui/drawer";
import { catchError } from "shared/ui/toast";
import { shortAddress } from "shared/web3/utils";

import { WalletDrawer } from "./drawer/drawer";

type Props = {
  connectContent?: ReactNode;
  switchChainContent?: ReactNode;
  className?: string;
} & ButtonProps;

export function ConnectButton({
  className,
  connectContent = "Connect Wallet",
  switchChainContent = "Switch Chain",
  ...props
}: Props) {
  const { address, isDisconnected, isConnecting, connector, chain } =
    useAccount();
  const chainId = useChainId();

  const { switchChain, isPending: isSwitchChainPending } = useSwitchChain();
  const { disconnect } = useDisconnect();
  const { connectAsync, connectors } = useConnect();

  const handleConnect = async () => {
    const metamaskConnector = connectors.find(
      (connector) => connector.name === "MetaMask"
    );
    const flaskConnector = connectors.find((connector) => {
      return connector.name === "MetaMask Flask";
    });

    const connector = flaskConnector ?? metamaskConnector;

    try {
      if (!connector)
        throw new Error("Install MetaMask or MetaMask flask wallets");

      await connectAsync({ connector });
    } catch (error) {
      disconnect();
      if (!(error instanceof ConnectorAlreadyConnectedError)) {
        catchError(error);
      }
    }
  };

  const btnProps: ComponentProps<typeof Button> = {
    className: twMerge("gap-x-1.5 whitespace-nowrap", className),
    isLoading: isConnecting,
    variant: "white/10",
    size: "38",
    ...props,
  };

  if (isDisconnected || isConnecting) {
    return (
      <Button
        {...btnProps}
        variant="spearmint"
        isLoading={isConnecting}
        onClick={handleConnect}
      >
        {connectContent}
      </Button>
    );
  }

  if (!chain) {
    return (
      <Button
        {...btnProps}
        variant="spearmint"
        isLoading={isSwitchChainPending}
        onClick={() => {
          switchChain(
            { connector: connector, chainId },
            {
              onError: (error) => {
                catchError(error);
              },
            }
          );
        }}
      >
        {switchChainContent}
      </Button>
    );
  }

  return (
    <Drawer>
      <Drawer.Trigger>
        <Button
          {...btnProps}
          onClick={() => {
            // disconnect();
          }}
        >
          {connector?.icon && (
            <img
              className="mr-2 size-5"
              src={connector.icon}
              alt={connector?.name}
            />
          )}
          {shortAddress(address, 4, 4)}
        </Button>
      </Drawer.Trigger>
      <Drawer.Content>
        <WalletDrawer />
      </Drawer.Content>
    </Drawer>
  );
}
